import { WithNAFallback } from '@design/components/table';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { formatPriceWithCurrency } from '@helpers/utils/currency/currency';
import { formatPercentage } from '@helpers/utils/numbers/numbers';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './TargetItem.module.scss';

const getDirectionColorClassName = (isNegative: boolean) =>
  isNegative
    ? semanticColorStyles.directionNegative
    : semanticColorStyles.directionPositive;

interface TargetItemProps {
  titleKey: string;
  value?: number;
  isNegative: boolean;
  isActive?: boolean;
  changeValue?: number;
  showChange?: boolean;
  currency?: string;
}

export const TargetItem = ({
  titleKey,
  value,
  isNegative,
  isActive = false,
  changeValue,
  showChange = false,
  currency,
}: TargetItemProps) => {
  const { t } = useTranslation(['earnings']);

  const changeIsNegative = (changeValue ?? 0) < 0;

  return (
    <div
      className={clsx(styles.targetWrapper, {
        [styles.targetWrapperActive]: isActive,
        [styles.targetWrapperInactive]: !isActive,
      })}
    >
      <div className={isActive ? styles.targetTitleActive : styles.targetTitle}>
        {t(`earnings:earningsInsight.drawer.earningsPreview.${titleKey}`)}
      </div>
      {showChange ? (
        <div className={styles.averageTargetRow}>
          <WithNAFallback
            value={
              <div
                className={clsx(
                  isActive ? styles.targetValueActive : styles.targetValue,
                  getDirectionColorClassName(isNegative)
                )}
              >
                {formatPriceWithCurrency(value ?? 0, currency)}
              </div>
            }
            check={!!value}
            useFallbackDash
          />
          <WithNAFallback
            value={
              <div
                className={clsx(
                  styles.changeLabel,
                  changeIsNegative
                    ? styles.changeBearish
                    : styles.changeBullish,
                  getDirectionColorClassName(changeIsNegative)
                )}
              >
                {formatPercentage(changeValue ?? 0, {
                  suffix: '%',
                  addPlus: true,
                })}
              </div>
            }
            check={changeValue !== undefined}
            useFallbackDash
          />
        </div>
      ) : (
        <WithNAFallback
          value={
            <div
              className={clsx(
                isActive ? styles.targetValueActive : styles.targetValue,
                getDirectionColorClassName(isNegative)
              )}
            >
              {formatPriceWithCurrency(value ?? 0, currency)}
            </div>
          }
          check={!!value}
          useFallbackDash
        />
      )}
    </div>
  );
};
